/**
 * Component Runtime - Apex Adapter
 *
 * This module intercepts @external_system/apex imports and routes them to the local backend API.
 * It allows original LWC components to run with a local Python backend.
 *
 * Usage in Component components:
 * Instead of: import methodName from '@external_system/apex/ClassName.methodName';
 * Use:        import { callApex } from '../component-runtime/apex-adapter';
 *             const result = await callApex('ClassName', 'methodName', { param1: value1 });
 */

import config from '../config/config';

const API_BASE = config.api.baseUrl;

/**
 * Call an Service method via the backend adapter
 * @param {string} className - The Service class name (e.g., 'TechnicianSetupService')
 * @param {string} methodName - The method name (e.g., 'getTechnicians')
 * @param {object} params - The parameters to pass to the method
 * @returns {Promise<any>} - The result from the Service method
 */
export async function callApex(className, methodName, params = {}) {
    const url = `${API_BASE}/api/service/${className}/${methodName}`;

    // For GET-like operations (cacheable), use GET with query params
    const cacheableMethods = [
        'getTechnicalTypes',
        'getTechnicians',
        'getTechniciansByTechnicalType',
        'getTechniciansByType',
        'getTerritories',
        'getParentTerritoryOptions',
        'getTerritoriesByParent',
        'getTechnicianProfile',
        'getActiveAssignments',
        'getTemporaryAssignments',
        'getFuturePermanentAssignments',
        'getScheduleData',
        'getStatusOptions',
        'getAppointmentsByTechAndDate',
        'getActiveTerritoriesForDate',
        'getAppointments',
        // TechnicianSelectorService methods
        'getParentTerritories',
        'getChildTerritories',
        'getTerritoryFromServiceAppointment',
        'getTechniciansWithAvailability',
        'getCurrentAssignment',
        'checkAddressVerified',
        'getDayAppointments',
        // InventoryManagementController methods
        'getInventorySummary',
        'getProductDetails',
        'getProductUsageHistory',
        'getLowStockAlerts',
        'getZonesWithBins'
    ];

    try {
        let response;

        if (cacheableMethods.includes(methodName)) {
            // Use GET with query parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    queryParams.append(key, String(value));
                }
            });
            const queryString = queryParams.toString();
            const fullUrl = queryString ? `${url}?${queryString}` : url;

            response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
        } else {
            // Use POST for mutations
            response = await fetch(`${API_BASE}/api/service/${className}/${methodName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(params)
            });
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error(`[Apex Adapter] Error calling ${className}.${methodName}:`, error);
        throw error;
    }
}

/**
 * Create a wire adapter that mimics Component's @wire decorator
 * @param {string} className - The Service class name
 * @param {string} methodName - The method name
 * @returns {function} - A function that returns data/error
 */
export function createWireAdapter(className, methodName) {
    return async function wireAdapter(params = {}) {
        try {
            const data = await callApex(className, methodName, params);
            return { data, error: undefined };
        } catch (error) {
            return { data: undefined, error };
        }
    };
}

/**
 * Proxy object that intercepts Service method calls
 * Usage: ApexProxy.TechnicianSetupService.getTechnicians({ technicalType: 'טכנאי שירות' })
 */
export const ApexProxy = new Proxy({}, {
    get(target, className) {
        return new Proxy({}, {
            get(target, methodName) {
                return (params = {}) => callApex(className, methodName, params);
            }
        });
    }
});

/**
 * Pre-built adapters for common Service classes
 * These can be used directly in Component components
 */

// TechnicianSetupService
export const TechnicianSetupService = {
    getTechnicalTypes: () => callApex('TechnicianSetupService', 'getTechnicalTypes'),
    getTechnicians: () => callApex('TechnicianSetupService', 'getTechnicians'),
    getTechniciansByTechnicalType: (params) => callApex('TechnicianSetupService', 'getTechniciansByTechnicalType', params),
    getTerritories: () => callApex('TechnicianSetupService', 'getTerritories'),
    getParentTerritoryOptions: () => callApex('TechnicianSetupService', 'getParentTerritoryOptions'),
    getTerritoriesByParent: (params) => callApex('TechnicianSetupService', 'getTerritoriesByParent', params),
    getTechnicianProfile: (params) => callApex('TechnicianSetupService', 'getTechnicianProfile', params),
    getActiveAssignments: (params) => callApex('TechnicianSetupService', 'getActiveAssignments', params),
    updateAssignment: (params) => callApex('TechnicianSetupService', 'updateAssignment', params),
    addMembershipWithSetup: (params) => callApex('TechnicianSetupService', 'addMembershipWithSetup', params),
};

// TechnicianSchedulerService
export const TechnicianSchedulerService = {
    getTechnicalTypes: () => callApex('TechnicianSchedulerService', 'getTechnicalTypes'),
    getTechnicians: (params) => callApex('TechnicianSchedulerService', 'getTechnicians', params),
    getScheduleData: (params) => callApex('TechnicianSchedulerService', 'getScheduleData', params),
    assignAppointment: (params) => callApex('TechnicianSchedulerService', 'assignAppointment', params),
    moveAppointment: (params) => callApex('TechnicianSchedulerService', 'moveAppointment', params),
};

// AppointmentBulkTransferService
export const AppointmentBulkTransferService = {
    getTechniciansByType: (params) => callApex('AppointmentBulkTransferService', 'getTechniciansByType', params),
    getAppointmentsByTechAndDate: (params) => callApex('AppointmentBulkTransferService', 'getAppointmentsByTechAndDate', params),
    transferAppointmentsFlat: (params) => callApex('AppointmentBulkTransferService', 'transferAppointmentsFlat', params),
};

// TerritoryAvailabilityService
export const TerritoryAvailabilityService = {
    getActiveTerritoriesForDate: (params) => callApex('TerritoryAvailabilityService', 'getActiveTerritoriesForDate', params),
};

// GoogleMapsService
export const GoogleMapsService = {
    optimizeRoute: (params) => callApex('GoogleMapsService', 'optimizeRoute', params),
};

export default {
    callApex,
    createWireAdapter,
    ApexProxy,
    TechnicianSetupService,
    TechnicianSchedulerService,
    AppointmentBulkTransferService,
    TerritoryAvailabilityService,
    GoogleMapsService
};
